import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Redirect, Route } from 'react-router-dom';
import './auth.css';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api', withCredentials: true });
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = useRef(null);
  const refresh = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get('/account/session');
      token.current = data.csrfToken;
      setUser(data.user);
      return data;
    } catch (err) {
      setError('Unable to connect to the login service. Please try again.');
      throw err;
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh().catch(() => {}); }, [refresh]);
  const request = useCallback(async (method, url, data) => {
    if (!token.current) await refresh();
    try {
      const response = await api.request({ method, url, data, headers: { 'X-CSRF-Token': token.current } });
      return response.data;
    } catch (err) {
      if (err.response && err.response.status === 401 && url !== '/account/login') setUser(null);
      throw new Error((err.response && err.response.data && err.response.data.message) || 'Unable to reach the server. Please try again.');
    }
  }, [refresh]);
  const login = async (username, password) => {
    const data = await request('post', '/account/login', { username, password });
    token.current = data.csrfToken;
    setUser(data.user);
  };
  const logout = async () => {
    await request('post', '/account/logout');
    token.current = null;
    setUser(null);
  };
  const signedOut = () => { token.current = null; setUser(null); };
  return <AuthContext.Provider value={{ user, loading, error, refresh, request, login, logout, signedOut }}>{children}</AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
export function ProtectedRoute({ component: Component, admin = false, ...rest }) {
  const { user, loading, error, refresh } = useAuth();
  return <Route {...rest} render={props => {
    if (loading) return <p className="account-page" role="status">Checking your session…</p>;
    if (error && !user) return <div className="account-page"><p role="alert">{error}</p><button onClick={() => refresh().catch(() => {})}>Retry</button></div>;
    if (!user) return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;
    if (admin && user.role !== 'admin') return <div className="account-page"><h1>Administrator access required</h1></div>;
    return <Component {...props} />;
  }} />;
}
